import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { TextField } from '@mui/material';
import { CircularProgress } from '@mui/material';
import { SharedAccessResourceType } from 'libs/end-to-end/src/auth';
import { useSharedAccessAuth } from 'apps/sigma2-ui/src/lib/hooks/useSharedAccessAuth';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';

type Props = {
  resourceId: string;
  resourceType: SharedAccessResourceType;
  onVerified?: () => void;
};

export const SharedAccessAuthForm: React.FC<Props> = ({
  resourceId,
  resourceType,
  onVerified,
}) => {
  const { status, requestCode, verifyCode, checkAccess } =
    useSharedAccessAuth();

  useEffect(() => {
    checkAccess({ resourceId, resourceType });
  }, [checkAccess, resourceId, resourceType]);

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');

  const codeInputRef = useRef<HTMLInputElement | null>(null);

  const isLoading =
    status.state === 'requesting-code' ||
    status.state === 'verifying' ||
    status.state === 'checking-access';

  const handleRequestCode = () => {
    requestCode({
      email,
      resourceId,
      resourceType,
    });
  };

  useEffect(() => {
    if (status.state === 'code-sent') {
      codeInputRef.current?.focus();
    }

    if (
      status.state === 'verified' ||
      (status.state === 'access-checked' && status.hasAccess)
    ) {
      onVerified?.();
    }
  }, [onVerified, status]);

  const handleVerifyCode = (codeOverride?: string) => {
    const finalCode = codeOverride ?? code;
    if (finalCode.length !== 6) return;

    verifyCode({
      email,
      code: finalCode,
      resourceId,
      resourceType,
    });
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6);

    if (/^\d{6}$/.test(pasted)) {
      e.preventDefault();
      setCode(pasted);
      handleVerifyCode(pasted);
    }
  };

  return (
    <Spacing direction="vertical" size="md" className="max-w-[600px] w-full">
      {/* Email Step */}
      {(status.state === 'request-failed' ||
        (status.state === 'access-checked' && !status.hasAccess)) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleRequestCode();
          }}
        >
          <Spacing
            direction="vertical"
            size="md"
            className="max-w-[600px] w-full"
          >
            <S2Typography
              variant="headline-6"
              translatedContent={'Access this resource'}
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              disabled={isLoading}
            />
            <button type="submit" style={{ display: 'none' }} />

            <Button
              variant="primary"
              behavior={{
                type: 'onClick',
                onClick: handleRequestCode,
              }}
              disabled={!email || isLoading}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Send Code'}
            </Button>
          </Spacing>
        </form>
      )}

      {/* Code Step */}
      {(status.state === 'code-sent' || status.state === 'verify-failed') && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleVerifyCode();
          }}
        >
          <Spacing
            direction="vertical"
            size="md"
            className="max-w-[600px] w-full"
          >
            <S2Typography
              variant="body-6"
              translatedContent={`Enter the 6-digit code sent to ${email}`}
            />

            <TextField
              label="Code"
              variant="outlined"
              value={code}
              onChange={(e) => {
                const digitsOnly = e.currentTarget.value
                  .replace(/\D/g, '')
                  .slice(0, 6);
                setCode(digitsOnly);
              }}
              inputRef={codeInputRef}
              onPaste={handlePaste}
              fullWidth
              disabled={isLoading}
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  maxLength: 6,
                  pattern: '[0-9]*',
                },
              }}
            />
            <button type="submit" style={{ display: 'none' }} />

            <Spacing
              direction="horizontal"
              size="sm"
              className="justify-between items-center"
            >
              <Button
                variant="primary"
                behavior={{
                  type: 'onClick',
                  onClick: () => handleVerifyCode(),
                }}
                disabled={code.length !== 6 || isLoading}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Verify Code'}
              </Button>

              <Button
                variant="outline"
                behavior={{
                  type: 'onClick',
                  onClick: handleRequestCode,
                }}
                disabled={isLoading}
              >
                Resend code
              </Button>
            </Spacing>
          </Spacing>
        </form>
      )}

      {/* Success */}
      {status.state === 'verified' && (
        <S2Typography
          variant="body-6"
          translatedContent={'Access granted. Redirecting...'}
          textColorVariant="green-800"
        />
      )}

      {/* Error */}
      {(status.state === 'request-failed' ||
        status.state === 'verify-failed') && (
        <S2Typography
          variant="body-6"
          translatedContent={status.errorMessage}
          textColorVariant="red-800"
        />
      )}

      {isLoading && <CircularProgress size={40} />}
    </Spacing>
  );
};
